import { useQuery } from '@apollo/client';

import { CertificateTypeEnum } from '@/shared/types/graphql/globals.v5';
import {
  MyDiplomasCustom,
  TFilterDiplomasCollaboratorOption,
} from '@/views/LearnDashboard/layouts/shared/DiplomasSection/type';

import GET_LIST_CERTIFICATES_COLLABORATOR_QUERY from '../graphql/get-list-certificates-collaborator-query.v5.graphql';
import {
  GetListCertificatesCollaboratorQuery,
  GetListCertificatesCollaboratorQueryVariables,
} from '../graphql/types/GetListCertificatesCollaboratorQuery';
import useDiplomasTranslation from './useDiplomasTranslations';

const PAGE_SIZE = 12;

export const useGetListCertificatesCollaborator = ({
  filterByTypeCertificate,
  currentPage,
}: {
  filterByTypeCertificate?: TFilterDiplomasCollaboratorOption;
  currentPage: number;
}) => {
  const { t } = useDiplomasTranslation();
  const { data, loading, error, refetch } = useQuery<
    GetListCertificatesCollaboratorQuery,
    GetListCertificatesCollaboratorQueryVariables
  >(GET_LIST_CERTIFICATES_COLLABORATOR_QUERY, {
    variables: {
      input: {
        certificate_type_filter_enum:
          filterByTypeCertificate && filterByTypeCertificate === 'ALL'
            ? undefined
            : filterByTypeCertificate,
        first: PAGE_SIZE,
        page: currentPage,
      },
    },
    context: {
      clientName: 'v5',
    },
  });

  const totalAllCertificates =
    data?.certificate_collaborator?.get_certificates?.metadata?.total_count;
  const totalCertificatesCourse =
    data?.certificate_collaborator?.get_certificates?.metadata
      ?.total_course_certificates;
  const totalCertificatesTracks =
    data?.certificate_collaborator?.get_certificates?.metadata
      ?.total_track_certificates;
  const totalParticipationCertificates =
    data?.certificate_collaborator?.get_certificates?.metadata
      ?.total_participation_certificates;

  const totalPages =
    data?.certificate_collaborator?.get_certificates?.page_info?.total_pages;

  const labelByTypeCertificate: Record<CertificateTypeEnum, string> = {
    [CertificateTypeEnum.CERTIFICATE_TYPE_COURSE]: t('CERTIFICATE'),
    [CertificateTypeEnum.CERTIFICATE_TYPE_TRACK]: t('CERTIFICATE'),
    [CertificateTypeEnum.CERTIFICATE_TYPE_ACADEMY]: t('CERTIFICATE'),
    [CertificateTypeEnum.CERTIFICATE_TYPE_CAREER]: t('CERTIFICATE'),
    [CertificateTypeEnum.CERTIFICATE_TYPE_CERTIFICATION]: t('CERTIFICATE'),
    [CertificateTypeEnum.PARTICIPATION_CERTIFICATE_TYPE_COURSE]: t('CONSTANCE'),
    [CertificateTypeEnum.PARTICIPATION_CERTIFICATE_TYPE_MINEDU]: t('CONSTANCE'),
    [CertificateTypeEnum.PARTICIPATION_CERTIFICATE_TYPE_SIXTH_ANNIVERSARY]:
      t('CONSTANCE'),
    [CertificateTypeEnum.PARTICIPATION_CERTIFICATE_TYPE_WORKSHOP]:
      t('CONSTANCE'),
  };

  const listCertificatesCollaborator: MyDiplomasCustom[] =
    data?.certificate_collaborator?.get_certificates?.edges?.map(
      certificate => {
        const isCertificateTrack =
          certificate?.node?.certificate_type ===
          CertificateTypeEnum.CERTIFICATE_TYPE_TRACK;

        const isCertificateCourse =
          certificate?.node?.certificate_type ===
          CertificateTypeEnum.CERTIFICATE_TYPE_COURSE;
        const isConstanceParticipationCourse =
          certificate?.node?.certificate_type ===
          CertificateTypeEnum.PARTICIPATION_CERTIFICATE_TYPE_COURSE;

        return {
          enrollment:
            isCertificateCourse || isConstanceParticipationCourse
              ? {
                  course: {
                    title: certificate?.node?.title || '',
                    originalId: certificate?.node?.id,
                    dashboardUrl: isConstanceParticipationCourse
                      ? certificate?.node?.attachment_url
                      : '',
                    promoImage: certificate?.node?.image_url,
                    __typename: 'CourseNode',
                  },
                  __typename: 'EnrollmentNode',
                }
              : undefined,
          enrollmentTrack: isCertificateTrack
            ? {
                track: {
                  originalId: certificate?.node?.id,
                  title: certificate?.node?.title || '',
                  promoImage: certificate?.node?.image_url,
                },
              }
            : undefined,
          attachmentUrl: certificate?.node?.attachment_url,
          diplomaUrl: isConstanceParticipationCourse
            ? certificate?.node?.attachment_url
            : `/diplomas/${certificate?.node?.code}`,
          code: certificate?.node?.code,
          createdAt: certificate?.node?.created_date,
          id: certificate?.node?.id,
          createdDate: certificate?.node?.created_date,
          enrollmentAcademySkillpath: null,
          enrollmentBundle: null,
          enrollmentCareer: null,
          imageThumbnailUrl: certificate?.node?.image_url,
          originalId: certificate?.node?.id,
          type: labelByTypeCertificate[
            certificate?.node?.certificate_type ||
              CertificateTypeEnum.CERTIFICATE_TYPE_COURSE
          ],
        };
      },
    ) || [];

  return {
    totalParticipationCertificates,
    totalCertificatesTracks,
    totalCertificatesCourse,
    totalAllCertificates,
    listCertificatesCollaborator,
    totalPages,
    loading,
    error,
    refetch,
  };
};
